import { motion } from 'framer-motion'
import React, { useCallback, useState, useEffect } from 'react'
import axios from 'axios'
import { Input } from 'baseui/input'
import { debounce } from 'lodash'
import { RiCloseFill } from 'react-icons/ri'
import { IoMdReturnLeft } from 'react-icons/io'
import { useRouter } from 'next/router'

function SearchModal({ closeModal }) {
  const router = useRouter()
  const [searchResults, setSearchResults] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const handleEsc = event => {
      if (event.keyCode === 27) {
        closeModal()
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [])

  const searchHandler = useCallback(
    debounce(async e => {
      if (e.target.value !== '') {
        const search = await axios.post('/api/rest/search', {
          searchTerm: e.target.value,
        })
        setSearchResults(search.data)
      } else {
        setSearchResults({})
      }
      setLoading(false)
    }, 250),
    []
  )

  return (
    <div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.1 }}
        className="top-0 left-0 w-full h-screen pt-24"
        style={{
          position: 'fixed',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.1 }}
          className=" shadow-2xl  overflow-hidden bg-white mx-auto"
          style={{
            width: '100%',
            maxWidth: '850px',
            position: 'relative',
            borderRadius: '7px',
          }}
        >
          <Input
            placeholder="Start typing to search..."
            id="searchInput"
            autoFocus
            onChange={e => {
              setLoading(true)
              searchHandler(e)
            }}
          />
          <RiCloseFill
            onClick={e => {
              closeModal()
              setSearchResults({})
            }}
            className="cursor-pointer hover:text-gray-900 text-gray-400 absolute text-2xl"
            style={{ top: '12px', right: '10px' }}
          />
          {loading && (
            <div className="text-lg font-medium px-6 py-6 text-center text-gray-500">
              Searching...
            </div>
          )}
          {!_.isEmpty(searchResults) && !loading && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.1 }}
              className="pt-3 pb-3 transition-all duration-500 ease-in-out transform hover:-translate-y-1 hover:scale-110 overflow-x-hidden"
              style={{ maxHeight: '500px' }}
            >
              {searchResults.onepagers.length === 0 &&
                searchResults.collections.length === 0 &&
                searchResults.saved.length === 0 &&
                searchResults.onepagertypes.length === 0 && (
                  <div className="text-lg font-medium px-6 py-6 text-center text-gray-500">
                    Nothing found for this search...
                  </div>
                )}

              {searchResults.onepagers.length > 0 && (
                <div className="px-3">
                  {searchResults.onepagers.map((result, index) => {
                    return (
                      <div
                        tabIndex="0"
                        className="cursor-pointer rounded hover:bg-gray-50 focus:bg-gray-50 px-4 py-2 font-medium flex items-center justify-between"
                        onClick={() => {
                          closeModal()
                          router.push(
                            '/workspace/template/builder/' + result.item._id
                          )
                        }}
                        onKeyPress={event => {
                          if (event.key === 'Enter') {
                            closeModal()
                            router.push(
                              '/workspace/template/builder/' + result.item._id
                            )
                          }
                        }}
                        key={index}
                      >
                        <div className="flex justify-between w-full">
                          <h4 className="text-lg text-gray-600 inline">
                            {result.item.title}
                          </h4>
                          <h3 className="inline font-medium uppercase text-xs inline-block mr-4 bg-yellow-50 rounded text-yellow-900 p-2">
                            Onepager{' '}
                            <span className="font-bold text-yellow-600">
                              - Grid
                            </span>
                          </h3>
                        </div>
                        <div style={{ width: '90px' }}>
                          <span className="text-gray-300 inline text-sm">
                            Select
                          </span>
                          <IoMdReturnLeft className="p-1 text-lg rounded inline bg-gray-300 text-white ml-1" />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {searchResults.onepagertypes.length > 0 && (
                <div className="px-3">
                  {searchResults.onepagertypes.map((result, index) => {
                    return (
                      <div
                        tabIndex="0"
                        className="cursor-pointer rounded hover:bg-gray-50 focus:bg-gray-50 px-4 py-2 font-medium flex items-center justify-between"
                        onClick={() => {
                          closeModal()
                          router.push(
                            '/workspace/template/v-builder/' + result.item._id
                          )
                        }}
                        onKeyPress={event => {
                          if (event.key === 'Enter') {
                            closeModal()
                            router.push(
                              '/workspace/template/v-builder/' + result.item._id
                            )
                          }
                        }}
                        key={index}
                      >
                        <div className="flex justify-between w-full">
                          <h4 className="text-lg text-gray-600 inline">
                            {result.item.title}
                          </h4>
                          <h3 className="inline font-medium uppercase text-xs inline-block mr-4 bg-yellow-50 rounded text-yellow-900 p-2">
                            Onepager{' '}
                            <span className="font-bold text-yellow-600">
                              - {result.item.type?.name?.trim()}
                            </span>
                          </h3>
                        </div>
                        <div style={{ width: '90px' }}>
                          <span className="text-gray-300 inline text-sm">
                            Select
                          </span>
                          <IoMdReturnLeft className="p-1 text-lg rounded inline bg-gray-300 text-white ml-1" />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {searchResults.collections.length > 0 && (
                <div className="px-3">
                  {searchResults.collections.map((result, index) => {
                    return (
                      <div
                        tabIndex="0"
                        className="cursor-pointer rounded hover:bg-gray-50 focus:bg-gray-50 px-4 py-2 font-medium flex items-center justify-between"
                        onClick={() => {
                          closeModal()
                          router.push(
                            '/workspace/edit-collection/' + result.item._id
                          )
                        }}
                        onKeyPress={event => {
                          if (event.key === 'Enter') {
                            closeModal()
                            router.push(
                              '/workspace/edit-collection/' + result.item._id
                            )
                          }
                        }}
                        key={index}
                      >
                        <div className="flex justify-between w-full">
                          <h4 className="text-lg text-gray-600 inline">
                            {result.item.title}
                          </h4>
                          <h3 className="inline font-medium uppercase text-xs inline-block mr-4 bg-purple-50 rounded text-purple-900 p-2">
                            Collection
                          </h3>
                        </div>
                        <div style={{ width: '90px' }}>
                          <span className="text-gray-300 inline text-sm">
                            Select
                          </span>
                          <IoMdReturnLeft className="p-1 text-lg rounded inline bg-gray-300 text-white ml-1" />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {searchResults.saved.length > 0 && (
                <div className="px-3">
                  {searchResults.saved.map((result, index) => {
                    return (
                      <div
                        tabIndex="0"
                        className="cursor-pointer rounded hover:bg-gray-50 focus:bg-gray-50 px-4 py-2 font-medium flex items-center justify-between"
                        onClick={() => {
                          closeModal()
                          window.open(
                            `https://${result.item.collection.subdomain}.onepager.app/?i=${result.item._id}`,
                            '_blank'
                          )
                        }}
                        onKeyPress={event => {
                          if (event.key === 'Enter') {
                            closeModal()
                            window.open(
                              `https://${result.item.collection.subdomain}.onepager.app/?i=${result.item._id}`,
                              '_blank'
                            )
                          }
                        }}
                        key={index}
                      >
                        <div className="flex justify-between w-full">
                          <h4 className="text-lg text-gray-600 inline">
                            {result.item.title}
                          </h4>
                          <h3 className="inline font-medium uppercase text-xs inline-block mr-4 bg-green-50 rounded text-green-800 p-2">
                            Saved OnePager
                          </h3>
                        </div>
                        <div style={{ width: '90px' }}>
                          <span className="text-gray-300 inline text-sm">
                            Select
                          </span>
                          <IoMdReturnLeft className="p-1 text-lg rounded inline bg-gray-300 text-white ml-1" />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </div>
  )
}

export default SearchModal
